import { injectable, Container, inject } from "../injection";
import { action, observable, computed, makeObservable } from "mobx";
import { LoggedInUserStore } from "../loggedInUser/LoggedInUserStore";
import { ErrorItem, IErrorOperation } from "./ErrorItem";
import { locationPrepend } from "../devServer/locationPrepend";
import { v4 as uuidv4 } from "uuid";
import { appDetails } from "./appDetails";

@injectable("singleton")
export class ErrorStore {
    @observable public errorItems: ErrorItem[] = [];

    private isPageUnloaded = false;
    private messageBusConnectionWasManuallyKilled = false;

    /** passing Container is bad practise. but it is needed here to avoid a initialising LoggedInUserStore immediately */
    constructor(@inject(Container) private container: Container) {
        makeObservable(this);
    }

    public setupErrorHandlers(): void {
        // set up main window error handler
        window.onerror = (
            event: Event | string,
            file?: string,
            line?: number,
            column?: number,
            error?: Error
        ) => {
            // reportError will (usually) return false. returning false from here will write an error to the console
            return this.reportError(error ?? event, undefined, {
                file,
                line,
                column
            });
        };

        // set up unhandled promise error handler
        window.onunhandledrejection = (event: PromiseRejectionEvent) => {
            const stop = this.reportError(event.reason, undefined, undefined, {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                isTimeout: event.reason?.__isTimeout
            });
            if (stop) {
                // no need to write an error to the console
                event.preventDefault();
            }
        };

        // set up unload
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        window.onunload = () => {
            this.isPageUnloaded = true;
        };

        // set legacy error functions (angularjs)
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        window.ErrorHandler = window.ErrorHandler || {};

        window.ErrorHandler.reportError = (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            error: any,
            name?: string,
            stackTrace?: {
                file?: string;
                line?: number;
                column?: number;
            },
            metadata?: {
                message?: RedDeer.Contracts.Base.Message;
                requestUrl?: string;
                isTimeout?: boolean;
            }
        ): boolean => {
            const stop = this.reportError(error, name, stackTrace, metadata);
            if (!stop) {
                // eslint-disable-next-line no-console
                console.error(error, name); // reportError doesn't log to the console
            }

            return true;
        };
        window.ErrorHandler.messageBusConnectionManuallyKilled =
            this.messageBusConnectionManuallyKilled;
    }

    @action
    public messageBusConnectionManuallyKilled = () => {
        this.messageBusConnectionWasManuallyKilled = true;
        this.hideErrors();
    };

    @action
    public hideErrors = () => {
        this.errorItems = [];
    };

    @action
    public reportError = (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error: any,
        name?: string,
        stackTrace?:
            | {
                  file?: string;
                  line?: number;
                  column?: number;
              }
            | string
            | null,
        metadata?: {
            message?: RedDeer.Contracts.Base.Message;
            requestUrl?: string;
            isTimeout?: boolean;
        }
    ): boolean => {
        if (this.isPageUnloaded) {
            // eslint-disable-next-line no-console
            console.error("error suppressed as page is unloaded", error);
            return false;
        }

        // if we know we killed the connection then we know any communication errors are a result of that
        // - so don't show them to the user (they will probably just occlude the logged-out dialog).
        if (this.messageBusConnectionWasManuallyKilled) {
            // eslint-disable-next-line no-console
            console.error(
                "error suppressed as connection manually ended",
                error
            );
            return false;
        }

        // This error can be ignored as per the author's suggestion
        // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
        // It doesn't break anything
        // Can be easily reproduced by playing video in full screen mode
        if (error === "ResizeObserver loop limit exceeded") {
            // eslint-disable-next-line no-console
            console.warn(
                "Error suppressed as it doesn't break anything, it only means that the ResizeObserver was not able to deliver all observation within a single animation frame",
                error
            );
            return false;
        }

        // defaults

        // message
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        let message: string = error + "";

        if (typeof stackTrace === "string" && stackTrace !== "") {
            message += stackTrace;
        } else if (typeof stackTrace === "object" && stackTrace !== null) {
            let file = stackTrace.file ?? "";
            const line = stackTrace.line ?? -1;
            const column = stackTrace.column ?? -1;

            // work out url base
            let urlBase: string;
            if (document.getElementsByTagName("base").length > 0) {
                urlBase = document.getElementsByTagName("base")[0]?.href ?? "";
            } else {
                const url = document.location.toString();
                const bits = url.split("://");
                const right = bits[1]?.split("/") ?? [];
                urlBase = (bits[0] ?? "") + "://" + (right[0] ?? "") + "/";
            }

            // trim base from file
            if (file.startsWith(urlBase)) {
                file = file.substring(urlBase.length);
            }

            if (file !== "") {
                message += " in " + file;
            }
            if (line > -1) {
                message += " on line " + line.toString();
            }
            if (column > -1) {
                message += " column " + column.toString();
            }
        }

        // stack trace
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const trace = error?.stack as string | undefined;

        // title
        const title = `${
            metadata?.isTimeout ? "A timeout" : "An error"
        } has occurred ${name ? `while ${name}` : "with the application"}.`;

        // try to suppress repeating errors
        // (errors can occasionally get to an infinite loop)
        let foundExisting = false;
        const time = Date.now();
        for (const errorDisplay of this.errorItems) {
            if (
                errorDisplay.time + 500 > time && // time limit of repeat
                errorDisplay.title === title &&
                errorDisplay.message === message &&
                errorDisplay.isTimeout === !!metadata?.isTimeout
            ) {
                foundExisting = true;
                errorDisplay.time = Date.now();
                errorDisplay.trace = trace;
                errorDisplay.count++;
                break;
            }
        }

        if (foundExisting) {
            // no need to continue further, as this error has already been reported
            return true;
        }

        const operation = this.getOrCreateOperation(metadata?.message);

        const currentUrl = document.location.href.toLocaleLowerCase();

        const requestUrl = metadata?.requestUrl;
        // suppress exception on UI as user left the original page
        const suppressException =
            requestUrl &&
            requestUrl.length > 0 &&
            requestUrl.toLocaleLowerCase() !== currentUrl;

        if (!suppressException) {
            this.errorItems.push(
                new ErrorItem(
                    title,
                    message,
                    !!metadata?.isTimeout,
                    trace ?? "",
                    operation
                )
            );
        }

        // json
        const json = {
            title,
            message,
            trace,
            url: document.location.href,
            requestUrl,
            appDetails: appDetails(),
            username: this.container.get(LoggedInUserStore).username,
            isExceptionShown: !suppressException,
            operation
        };

        const data = new FormData();
        data.append("_json", JSON.stringify(json));
        void fetch(`${locationPrepend()}/Timeline/LogError`, {
            method: "POST",
            body: data,
            credentials: "include"
        });

        return false;
    };

    private getOrCreateOperation(
        operation?: RedDeer.Contracts.Base.Message
    ): IErrorOperation {
        const operationId: string = operation?.MessageId ?? "";

        let operationCode: string;
        if (operationId.length > 8) {
            operationCode = operationId.substring(0, 8).toLocaleUpperCase();
        } else {
            operationCode = operationId;
        }
        // Client-side operations don't have operationId
        // We still need to assign code in order to track client-side events
        if (!operationCode) {
            operationCode = uuidv4().substring(0, 8).toLocaleUpperCase();
        }

        return {
            id: operationId,
            code: operationCode,
            name: operation?.MessageType ?? "Undefined"
        };
    }

    @computed
    get errorMessage(): string {
        if (this.errorItems.length === 0) {
            return "";
        }

        const max = 10;
        const show = Math.min(this.errorItems.length, max);
        const messages: string[] = [];

        for (let i = 0; i < show; i++) {
            const error = this.errorItems[i];
            if (error) {
                messages.push(
                    `${new Date(error.time).toISOString()}
                ${error.title}
                ${
                    error.operation.code
                        ? `Error code: ${error.operation.code}`
                        : ""
                }
                ${
                    error.operation.name
                        ? `Operation Name: ${error.operation.name}`
                        : ""
                }
                ${
                    error.operation.id
                        ? `Operation Id: ${error.operation.id}`
                        : ""
                }
                ${error.message}\n- ${error.trace?.split("\n").join("\n -") ?? ""}`
                );
            }
        }

        if (this.errorItems.length > max) {
            messages.push(
                `(Only showing ${max.toString()} of ${this.errorItems.length.toString()} messages)`
            );
        }

        return messages.join("\n\n");
    }
}
